import React, { useRef, useState, useEffect } from 'react';
import Matter from 'matter-js';

const FallingText = ({
  text = '',
  highlightWords = [],
  trigger = 'auto',
  gravity = 1,
  mouseConstraintStiffness = 0.2,
  fontSize = '1rem'
}) => {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [effectStarted, setEffectStarted] = useState(false);

  useEffect(() => {
    if (!textRef.current) return;
    const words = text.split(' ');
    textRef.current.innerHTML = words
      .map(
        (word) =>
          `<span class="inline-block mx-3 my-2 select-none font-normal text-transparent cursor-pointer hover:text-white transition-colors duration-300" style="-webkit-text-stroke: 1px rgba(255,255,255,0.6);white-space: nowrap;">${word}</span>`
      )
      .join(' ');
  }, [text, highlightWords]);

  useEffect(() => {
    if (trigger === 'auto') {
      setEffectStarted(true);
      return;
    }
    if (trigger === 'scroll' && containerRef.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setEffectStarted(true);
            observer.disconnect();
          }
        },
        { threshold: 0.1 }
      );
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    }
  }, [trigger]);

  useEffect(() => {
    if (!effectStarted || !containerRef.current || !textRef.current) return;

    const { Engine, World, Bodies, Body, Runner, Mouse, MouseConstraint } = Matter;
    const containerRect = containerRef.current.getBoundingClientRect();
    const width = containerRect.width;
    const height = containerRect.height;
    if (width <= 0 || height <= 0) return;

    const isMobile = window.innerWidth < 768;
    const engine = Engine.create();
    engine.world.gravity.y = gravity;

    const options = { isStatic: true };
    const walls = [
      Bodies.rectangle(width / 2, height + 25, width, 50, options),
      Bodies.rectangle(-25, height / 2, 50, height, options),
      Bodies.rectangle(width + 25, height / 2, 50, height, options),
      Bodies.rectangle(width / 2, -100, width, 50, options)
    ];

    const wordBodies = [...textRef.current.querySelectorAll('span')].map((elem) => {
      const rect = elem.getBoundingClientRect();
      const x = rect.left - containerRect.left + rect.width / 2;
      const y = rect.top - containerRect.top + rect.height / 2;
      const body = Bodies.rectangle(x, y, rect.width, rect.height, {
        restitution: 0.6,
        frictionAir: 0.02,
        friction: 0.1
      });
      Body.setVelocity(body, { x: (Math.random() - 0.5) * 3, y: (Math.random() - 0.5) * 3 });
      Body.setAngularVelocity(body, (Math.random() - 0.5) * 0.05);
      return { elem, body };
    });

    wordBodies.forEach(({ elem, body }) => {
      elem.style.position = 'absolute';
      elem.style.left = `${body.position.x}px`;
      elem.style.top = `${body.position.y}px`;
      elem.style.transform = 'translate(-50%, -50%)';
      elem.style.willChange = 'transform';
    });

    const worldBodies = [...walls, ...wordBodies.map((wb) => wb.body)];

    if (!isMobile) {
      const mouse = Mouse.create(containerRef.current);
      worldBodies.push(
        MouseConstraint.create(engine, {
          mouse,
          constraint: { stiffness: mouseConstraintStiffness, render: { visible: false } }
        })
      );
    }

    World.add(engine.world, worldBodies);
    const runner = Runner.create();
    Runner.run(runner, engine);

    let frameId;
    let visible = true;
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      runner.enabled = visible;
    });
    observer.observe(containerRef.current);

    const updateLoop = () => {
      if (visible) {
        wordBodies.forEach(({ body, elem }) => {
          elem.style.left = '0px';
          elem.style.top = '0px';
          elem.style.transform = `translate3d(${body.position.x}px, ${body.position.y}px, 0) translate(-50%, -50%) rotate(${body.angle}rad)`;
        });
      }
      frameId = requestAnimationFrame(updateLoop);
    };
    updateLoop();

    return () => {
      cancelAnimationFrame(frameId);
      observer.disconnect();
      Runner.stop(runner);
      World.clear(engine.world, false);
      Engine.clear(engine);
    };
  }, [effectStarted, gravity, mouseConstraintStiffness, fontSize]);

  return (
    <div
      ref={containerRef}
      className="relative z-[1] w-full h-full cursor-pointer overflow-hidden"
      style={{ touchAction: 'pan-y' }}
    >
      <div
        ref={textRef}
        className="w-full h-full p-10 flex flex-wrap justify-center content-start gap-4"
        style={{ fontSize, lineHeight: 1.4 }}
      />
    </div>
  );
};

export default FallingText;
